import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { apiPost } from "../../services/api/apiClient";

// Initial state
const initialState = {
  paymentStatus: "idle",
  paymentError: null,

  invoiceId: null,
  invoiceStatus: "idle",
  invoiceError: null,

  name: "",
  email: "",
  invoiceNumber: "",
  amount: "",
  amountLeft: "",
  inputAmount: "0.00", // This will be used to track the amount input by the user
  scheduledAmount: "",
  routingNumber: "",
  bankAccount: "",
  // What the invoice's own book needs. The lookup answers with the book and the
  // list of fields the form must collect, so the page renders the right form
  // without knowing anything about either system.
  source: "",
  requiredFields: [] as string[],
  accountType: "PERSONAL_CHECKING",
  phone: "",
  status: null,
};

// Async thunk for charging a public payment
export const chargePublicPayment = createAsyncThunk(
  "paymentForm/chargePublicPayment",
  async (formData: any, { rejectWithValue }) => {
    try {
      const data = await apiPost({
        endpoint: "/public/process-payment",
        data: formData,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to process payment");
    }
  }
);

export const lookUpInvoice = createAsyncThunk(
  "paymentForm/lookUpInvoice",
  async ({ invoiceNumber, token, signal }: any, { rejectWithValue }) => {
    try {
      // A payment link names its invoice with a token; the number is the
      // walk-up case. The API answers with the same shape for both.
      const data = await apiPost({
        endpoint: "/public/lookup-invoice",
        data: token ? { token } : { invoiceNumber },
        signal,
      });
      return data;
    } catch (error: any) {
      // Don't show error if request was aborted
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message || "Failed to look up invoice");
    }
  }
);

// Slice
const paymentFormSlice = createSlice({
  name: "paymentForm",
  initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    onChange: (state: any, action: PayloadAction<any>) => {
      const { name, value } = action.payload;
      (state as any)[name] = value;
      if (name === "invoiceNumber") {
        state.invoiceId = null; // Reset invoiceId when invoiceNumber changes
        state.amount = ""; // Reset amount when invoiceNumber changes
        state.amountLeft = ""; // Reset amountLeft when invoiceNumber changes
        state.scheduledAmount = ""; // Reset scheduledAmount when invoiceNumber changes
        state.invoiceStatus = "idle"; // Reset invoice status
        state.inputAmount = ""; // Reset inputAmount
        state.status = null; // Reset status
      }
    },
    clearMessages: (state: any) => {
      state.pmhPageStatus = "idle";
      state.pmhPageError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(chargePublicPayment.pending, (state) => {
        state.paymentStatus = "loading";
        state.paymentError = null;
      })
      .addCase(chargePublicPayment.fulfilled, (state) => {
        state.paymentStatus = "succeeded";
        state.paymentError = null;
      })
      .addCase(chargePublicPayment.rejected, (state: any, action: PayloadAction<any>) => {
        state.paymentStatus = "failed";
        state.paymentError = action.payload;
      })
      .addCase(lookUpInvoice.pending, (state) => {
        state.invoiceStatus = "loading";
        state.invoiceError = null;
      })
      .addCase(lookUpInvoice.fulfilled, (state: any, action: PayloadAction<any>) => {
        state.invoiceStatus = "succeeded";
        state.invoiceError = null;
        state.invoiceId = action.payload?.id || null;
        state.source = action.payload?.source || "";
        state.requiredFields = Array.isArray(action.payload?.requiredFields)
          ? action.payload.requiredFields
          : [];
        state.amount = action.payload?.amount || 0;
        state.scheduledAmount = action.payload?.scheduledAmount || 0;

        const amountLeft = action.payload?.amountLeft || 0;
        state.amountLeft =
          typeof amountLeft === "number"
            ? amountLeft.toFixed(2)
            : parseFloat(amountLeft).toFixed(2);
        state.inputAmount = state.amountLeft; // Set inputAmount to amountLeft

        state.status = action.payload?.paymentStatus || null;
      })
      .addCase(lookUpInvoice.rejected, (state: any, action: PayloadAction<any>) => {
        // Don't update state if request was cancelled
        if (action.payload === "Request cancelled") {
          return;
        }
        state.invoiceStatus = "failed";
        state.invoiceError = action.payload;
        state.invoiceId = null;
        state.source = "";
        state.requiredFields = [];
        state.amount = "";
        state.amountLeft = "";
        state.scheduledAmount = "";
        state.inputAmount = "0.00"; // Reset inputAmount
        state.status = null;
      });
  },
});

export const { resetState, clearMessages, onChange } = paymentFormSlice.actions;

export default paymentFormSlice.reducer;
